// @ts-nocheck
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "@libraryadmin/components/layout/DashboardLayout";
import { Button } from "@libraryadmin/components/ui/button";
import { usePagination } from "@libraryadmin/hooks/usePagination";
import { DataTablePagination } from "@libraryadmin/components/ui/data-table-pagination";
import { Input } from "@libraryadmin/components/ui/input";
import { Label } from "@libraryadmin/components/ui/label";
import { Textarea } from "@libraryadmin/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@libraryadmin/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@libraryadmin/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@libraryadmin/components/ui/table";
import { Badge } from "@libraryadmin/components/ui/badge";
import { Skeleton } from "@libraryadmin/components/ui/skeleton";
import { BookCheck, Search, CheckCircle, Plus } from "lucide-react";
import { useToast } from "@libraryadmin/hooks/use-toast";
import {
  getBooks,
  getPendingReturns,
  receiveBook,
} from "@libraryadmin/services/api";
import type { ProcessBookReturnPayload } from "@libraryadmin/services/api";
import type { BookIssue } from "@libraryadmin/data/seed";

const BookReceive = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [showProcessDialog, setShowProcessDialog] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedBook, setSelectedBook] = useState<BookIssue | null>(null);
  const [processedToday, setProcessedToday] = useState(0);

  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [returnCondition, setReturnCondition] = useState("Good");
  const [returnDate, setReturnDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [fineAmount, setFineAmount] = useState(0);
  const [returnRemarks, setReturnRemarks] = useState("");

  const [manualMemberId, setManualMemberId] = useState("");
  const [manualBookNo, setManualBookNo] = useState("");
  const [manualIssueId, setManualIssueId] = useState("");

  const {
    data: pending = [],
    isLoading,
    isError,
    error: pendingError,
  } = useQuery({
    queryKey: ["pending-returns"],
    queryFn: getPendingReturns,
  });

  const { data: books = [] } = useQuery({
    queryKey: ["books"],
    queryFn: getBooks,
  });

  const { mutate: doReceive, isPending: isReceiving } = useMutation({
    mutationFn: (payload: ProcessBookReturnPayload) => receiveBook(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pending-returns"] });
      queryClient.invalidateQueries({ queryKey: ["book-issues"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] });
      setShowConfirm(false);
      setShowProcessDialog(false);
      toast({
        title: "Book Received",
        description: `"${selectedBook?.bookTitle || manualBookNo}" has been returned successfully.`,
      });
      setSelectedBook(null);
      setManualMemberId("");
      setManualBookNo("");
      setManualIssueId("");
      setReturnCondition("Good");
      setReturnDate(new Date().toISOString().split("T")[0]);
      setFineAmount(0);
      setReturnRemarks("");
      setProcessedToday(count => count + 1);
    },
    onError: error => {
      toast({
        title: "Unable to process return",
        description:
          error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    },
  });

  const filtered = pending.filter(b => {
    const matchesSearch =
      b.memberName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      b.bookTitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
      b.bookNo.toLowerCase().includes(searchQuery.toLowerCase()) ||
      b.memberId.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus =
      filterStatus === "all" ||
      (filterStatus === "overdue"
        ? (b.daysRemaining ?? 0) < 0
        : (b.daysRemaining ?? 0) >= 0);

    return matchesSearch && matchesStatus;
  });

  const handleReceiveClick = (book: BookIssue) => {
    setSelectedBook(book);
    setShowConfirm(true);
  };

  const resolveBookNo = (issue: BookIssue) => {
    return (
      books.find(
        book => book.id === issue.bookId || book.bookNo === issue.bookNo
      )?.bookNo || issue.bookNo
    );
  };

  const pendingMembers = Array.from(
    new Map(
      pending.map(issue => [
        issue.memberId,
        { code: issue.memberId, name: issue.memberName },
      ])
    ).values()
  );

  const memberPendingBooks = pending.filter(
    issue => issue.memberId === manualMemberId
  );

  const submitReturn = () => {
    const memberCode = selectedBook?.memberId || manualMemberId;
    const bookNo = selectedBook ? resolveBookNo(selectedBook) : manualBookNo;

    doReceive({
      memberCode,
      bookNo,
      conditionOnReturn: returnCondition,
      returnDate,
      fineAmount,
      remarks: returnRemarks,
    });
  };

  const overdueCount = pending.filter(b => (b.daysRemaining ?? 0) < 0).length;

  const {
    currentPage,
    totalPages,
    itemsPerPage,
    paginatedData,
    handlePageChange,
    handleItemsPerPageChange,
  } = usePagination(filtered);

  return (
    <DashboardLayout
      title="Book Returns & Processing"
      subtitle="Receive returned books, check conditions, and manage pending returns"
    >
      <div className="space-y-6">
        {/* Stats strip */}
        <div className="grid grid-cols-3 gap-3">
          {[
            {
              label: "Pending Returns",
              value: pending.length,
              color: "text-primary",
            },
            {
              label: "Overdue Defaulters",
              value: overdueCount,
              color: "text-destructive",
            },
            {
              label: "Processed Today",
              value: processedToday,
              color: "text-success",
            },
          ].map(s => (
            <div
              key={s.label}
              className="rounded-xl border border-border/40 bg-card p-4 shadow-sm"
            >
              <p className="text-xs text-muted-foreground">{s.label}</p>
              <p className={`mt-1 text-2xl font-bold ${s.color}`}>
                {isLoading ? "—" : s.value}
              </p>
            </div>
          ))}
        </div>

        {/* Main List Section */}
        <div className="form-section">
          <div className="mb-6 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
            <div>
              <h2 className="text-lg font-bold text-foreground">
                Pending Returns Queue
              </h2>
              <p className="text-xs text-muted-foreground">
                Showing {filtered.length} books pending return
              </p>
            </div>

            <Button
              onClick={() => {
                setSelectedBook(null);
                setManualMemberId("");
                setManualBookNo("");
                setManualIssueId("");
                setShowProcessDialog(true);
              }}
              className="btn-secondary w-full sm:w-auto"
            >
              <Plus className="mr-2 h-4 w-4" /> Process Return
            </Button>
          </div>

          {/* Filters Bar */}
          <div className="mb-6 grid grid-cols-1 gap-3 rounded-xl border border-border/50 bg-muted/30 p-3 sm:grid-cols-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search member, book title, book no..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="input-field pl-9 text-xs"
              />
            </div>

            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="input-field h-9 text-xs">
                <SelectValue placeholder="Status Filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Pending Returns</SelectItem>
                <SelectItem value="normal">On-Time Returns</SelectItem>
                <SelectItem value="overdue">Overdue Only</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Pending Table */}
          <div className="overflow-hidden rounded-xl border border-border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead>Member</TableHead>
                  <TableHead>Book Title & No</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead>Return Status</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  Array.from({ length: 4 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 5 }).map((_, j) => (
                        <TableCell key={j}>
                          <Skeleton className="h-4 w-full" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : isError ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="py-12 text-center text-destructive"
                    >
                      {pendingError instanceof Error
                        ? pendingError.message
                        : "Unable to load pending returns."}
                    </TableCell>
                  </TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="py-12 text-center text-muted-foreground"
                    >
                      No pending returns match criteria
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedData.map(book => (
                    <TableRow key={book.id} className="table-row-hover">
                      <TableCell>
                        <div>
                          <p className="text-sm font-semibold text-foreground">
                            {book.memberName}
                          </p>
                          <p className="font-mono text-xs text-muted-foreground">
                            {book.memberId}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-sm font-semibold text-primary">
                            {book.bookTitle}
                          </p>
                          <p className="font-mono text-xs text-muted-foreground">
                            {book.bookNo}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs font-semibold text-muted-foreground">
                        {book.dueDate}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={
                            (book.daysRemaining ?? 0) < 0
                              ? "badge-destructive"
                              : (book.daysRemaining ?? 0) <= 3
                                ? "badge-warning"
                                : "badge-success"
                          }
                        >
                          {(book.daysRemaining ?? 0) < 0
                            ? `${Math.abs(book.daysRemaining!)} days overdue`
                            : `${book.daysRemaining} days left`}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleReceiveClick(book)}
                          className="gap-1 font-semibold text-secondary hover:bg-secondary/10 hover:text-secondary"
                        >
                          <CheckCircle className="h-4 w-4" /> Receive
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <DataTablePagination
              currentPage={currentPage}
              totalPages={totalPages}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </div>
        </div>
      </div>

      {/* Manual Process Return Modal */}
      <Dialog open={showProcessDialog} onOpenChange={setShowProcessDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-foreground">
              <BookCheck className="h-5 w-5 text-primary" /> Process Manual Book
              Return
            </DialogTitle>
            <DialogDescription>
              Input member ID and book number to scan or mark return.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2 text-xs">
            <div className="space-y-1">
              <Label>Member ID *</Label>
              <Select
                value={manualMemberId}
                onValueChange={value => {
                  setManualMemberId(value);
                  setManualBookNo("");
                  setManualIssueId("");
                  setSelectedBook(null);
                }}
              >
                <SelectTrigger className="input-field font-mono">
                  <SelectValue placeholder="Select a member" />
                </SelectTrigger>
                <SelectContent>
                  {pendingMembers.map(member => (
                    <SelectItem key={member.code} value={member.code}>
                      {member.code} — {member.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Book No *</Label>
              <Select
                value={manualIssueId}
                onValueChange={issueId => {
                  const issue = memberPendingBooks.find(
                    pendingIssue => pendingIssue.id === issueId
                  );
                  setManualIssueId(issueId);
                  setSelectedBook(issue || null);
                  setManualBookNo(issue ? resolveBookNo(issue) : "");
                }}
                disabled={!manualMemberId}
              >
                <SelectTrigger className="input-field font-mono">
                  <SelectValue placeholder="Select an issued book" />
                </SelectTrigger>
                <SelectContent>
                  {memberPendingBooks.map(issue => (
                    <SelectItem key={issue.id} value={issue.id}>
                      {resolveBookNo(issue)} — {issue.bookTitle}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Condition on Return</Label>
              <Select
                value={returnCondition}
                onValueChange={setReturnCondition}
              >
                <SelectTrigger className="input-field">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {["Excellent", "Good", "Fair", "Damaged"].map(c => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Return Date</Label>
                <Input
                  type="date"
                  className="input-field"
                  value={returnDate}
                  onChange={e => setReturnDate(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label>Fine Amount</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  className="input-field"
                  value={fineAmount}
                  onChange={e => setFineAmount(Number(e.target.value))}
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Remarks</Label>
              <Textarea
                placeholder="Returned in good condition via manual scan."
                className="input-field min-h-[70px]"
                value={returnRemarks}
                onChange={e => setReturnRemarks(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter className="border-t pt-3">
            <Button
              variant="outline"
              onClick={() => setShowProcessDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={() => setShowConfirm(true)}
              disabled={!selectedBook}
              className="btn-secondary"
            >
              Receive Book
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Book Return</DialogTitle>
            <DialogDescription>
              Mark <strong>"{selectedBook?.bookTitle || manualBookNo}"</strong>{" "}
              as returned from member?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirm(false)}>
              Cancel
            </Button>
            <Button
              onClick={submitReturn}
              disabled={isReceiving}
              className="btn-secondary"
            >
              {isReceiving ? "Processing…" : "Confirm Return"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default BookReceive;
